import { create } from 'zustand';
import {
  AJUSTES_POR_DEFECTO,
  CLAVE_AJUSTE,
  leerAjusteGuardado,
  puedeUsarCanal,
  type AjustesPermisos,
} from '@shared/permisos';

/**
 * Sprint 64 — Los tres ajustes del negocio, del lado de la pantalla.
 *
 * **Esto NO es control de acceso.** La autorización vive en `safeHandleConPermiso`, en el
 * servidor, y ahí seguirá aunque alguien edite este store desde las devtools. Lo que hace
 * este archivo es que la pantalla **no mienta**: sin él, un mesero ve el botón "Cobrar",
 * teclea el efectivo recibido, y hasta el último clic se enteraría de que no tiene permiso.
 * Ofrecer un botón que va a rebotar es peor que no ofrecerlo.
 *
 * Se carga una vez al entrar y se refresca cuando el dueño mueve un interruptor. Los
 * defaults —los más restrictivos— aplican mientras carga: es preferible que un botón
 * aparezca un instante después que autorizarlo por un instante de más.
 */

interface Estado {
  ajustes: AjustesPermisos;
  cargado: boolean;
  cargar: () => Promise<void>;
  /** ¿Este rol puede este canal, con los ajustes de ESTE negocio? */
  puede: (rol: string | null | undefined, canal: string) => boolean;
}

export const useAjustesPermisos = create<Estado>((set, get) => ({
  ajustes: AJUSTES_POR_DEFECTO,
  cargado: false,

  cargar: async () => {
    const claves = Object.keys(CLAVE_AJUSTE) as Array<keyof AjustesPermisos>;
    const leidos = await Promise.all(
      claves.map(async (clave) => {
        const res = await window.api.config.obtener(CLAVE_AJUSTE[clave]);
        // El mismo lector que usa el servidor: dos reglas para el mismo dato acaban en un
        // "a veces el botón no aparece" imposible de reproducir.
        const valor = res.ok
          ? leerAjusteGuardado(res.data, AJUSTES_POR_DEFECTO[clave])
          : AJUSTES_POR_DEFECTO[clave];
        return [clave, valor] as const;
      }),
    );
    set({
      ajustes: Object.fromEntries(leidos) as unknown as AjustesPermisos,
      cargado: true,
    });
  },

  puede: (rol, canal) => puedeUsarCanal(rol, canal, get().ajustes),
}));
